import { useEffect, useState } from "react";

import {
  useAccountSettings,
  useAuth,
  useBalances,
  useDebounce,
  useMerchantApiQuery,
  useMerchantApiQueryWithParams,
  useShowOnboardingRequired,
} from "../../hooks";
import {
  IOnboardingStatus,
  IViewActivatedLenderBorrowerLink,
  NoMeta,
} from "../../interfaces-and-types";
import { View } from "./View";
import { IViewBorrowerLineOfCredit } from "../../interfaces-and-types/switchapp-api/loans/dtos/IViewBorrowerLineOfCredit";
import { formatAsCurrency, getCurrencySymbol } from "../../utils";

interface ITenantAndLiveModeStatus {
  active_tenant_id: string | null;
  is_live: boolean;
}
export const LoanOfferAvailableBanner = () => {
  const { active_tenant_id, user } = useAuth();
  const { data } = useAccountSettings();
  const { activeBalanceCurrency } = useBalances();

  const {
    isSuccess,
    data: activatedLenderBorrowerLinks,
    refetch: refetchLoanOffers,
  } = useMerchantApiQueryWithParams<
    ITenantAndLiveModeStatus,
    IViewActivatedLenderBorrowerLink[],
    NoMeta
  >(
    [
      "getLenderBorrowerLinks",
      { active_tenant_id, is_live: data?.is_live || false },
    ],
    {
      enabled: !!data,
    },
    (templateUrl) => templateUrl,
    {
      baseURL: process.env.REACT_APP_LOANS_BORROWER_API_BASE_URL!,
      hideResponseMessage: true,
      hideApiErrorMessge: true,
      hideAxiosErrorMessages: true,
    }
  );

  useEffect(() => {
    refetchLoanOffers();
  }, [refetchLoanOffers, active_tenant_id, data?.is_live]);

  const [maxLineOfCredit, setMaxLineOfCredit] =
    useState<IViewBorrowerLineOfCredit | null>(null);

  const [showLoanAppDashboard, setShowLoanAppDashboard] =
    useState<boolean>(false);
  const [isLoanAppLoaded, setLoanAppLoaded] = useState<boolean>(false);

  const [hideBanner, setHideBanner] = useState<boolean>(false);
  const handleHideBanner = () => {
    setHideBanner((status) => !status);
  };

  const locFilter = (loc: IViewBorrowerLineOfCredit): boolean =>
    loc.currency === activeBalanceCurrency &&
    loc.total_loan_amount_available > 0;

  const hasLineOfCreditForCurrency =
    isSuccess &&
    !!activatedLenderBorrowerLinks &&
    !!activatedLenderBorrowerLinks?.length &&
    !!activatedLenderBorrowerLinks.filter(
      (lnk) => !!lnk.borrower_lines_of_credit.find(locFilter) !== undefined
    );

  useEffect(() => {
    if (isSuccess) {
      if (!hasLineOfCreditForCurrency) return;

      const sortedLinesOfCredit = activatedLenderBorrowerLinks
        .map((lnk) => lnk.borrower_lines_of_credit.find(locFilter))
        .sort((loc1, loc2) =>
          !loc1
            ? -1
            : !loc2
            ? 1
            : loc1.total_loan_amount_available -
              loc2.total_loan_amount_available
        );

      if (!!sortedLinesOfCredit.length)
        setMaxLineOfCredit(sortedLinesOfCredit[0]!);
    }
  }, [isSuccess, hasLineOfCreditForCurrency]);

  const titleNoLineOfCredit = (
    <>You are eligible for a loan on SwitchApp Loans</>
  );

  const titleWithLineOfCredit = (
    <>
      You are eligible for a loan of{" "}
      <span className="main-loan--title text-danger font-ctm--black">
        {getCurrencySymbol(activeBalanceCurrency ?? "")}
        {!maxLineOfCredit?.total_loan_amount_available
          ? "..."
          : formatAsCurrency(maxLineOfCredit?.total_loan_amount_available!)}
      </span>{" "}
      on SwitchApp Loans
    </>
  );

  const subtitle = (
    <span className="">
      A lender is ready to issue the loan to you. Click here to apply.
    </span>
  );

  const iframeUrlTarget =
    process.env.REACT_APP_LOANS_BORROWER_DASHBOARD_HOMEPAGE_URL!;

  const handleShowLoanAppInIframe = () => {
    setLoanAppLoaded(true);
    setShowLoanAppDashboard(true);
  };

  const toggleShowLoanAppDashboard = () => {
    setShowLoanAppDashboard((status) => !status);
  };

  const debounceComplete = useDebounce(
    2000,
    isSuccess && hasLineOfCreditForCurrency
  );

  if (!hasLineOfCreditForCurrency || !debounceComplete) return <></>;

  return (
    <View
      title={
        !!maxLineOfCredit?.total_loan_amount_available
          ? titleWithLineOfCredit
          : titleNoLineOfCredit
      }
      subtitle={subtitle}
      iframeUrlTarget={iframeUrlTarget}
      isLoanAppLoaded={isLoanAppLoaded}
      showLoanAppDashboard={showLoanAppDashboard}
      toggleShowLoanAppDashboard={toggleShowLoanAppDashboard}
      handleShowLoanAppInIframe={handleShowLoanAppInIframe}
      hideBanner={hideBanner}
      handleHideBanner={handleHideBanner}
    />
  );
};
